<template>
  <b-modal
    id="AssignRoleDialog"
    ref="AssignRoleDialog"
    :title="title"
    centered
    no-close-on-backdrop
    @hide="onHide"
    @ok.prevent="submit"
  >
    <div v-if="targetUser" class="target-strip mb-3">
      <div class="text-muted small">มอบบทบาทให้</div>
      <div class="font-weight-bold">
        {{ targetUser.firstNameTh }} {{ targetUser.lastNameTh }}
        <span v-if="scope === 'clinic'" class="text-muted small font-weight-normal">
          · ระดับคลินิก
        </span>
      </div>
    </div>

    <b-form-group label="เลือกบทบาทที่จะมอบ" class="mb-3">
      <div v-if="roleChoices.length === 0" class="text-muted small">
        คุณไม่มีสิทธิ์มอบบทบาทใด ๆ ในขอบเขตนี้
      </div>
      <div v-else class="role-card-list">
        <button
          v-for="opt in roleChoices"
          :key="opt.value"
          type="button"
          class="role-card"
          :class="{ selected: roleKey === opt.value }"
          @click="roleKey = opt.value"
        >
          <span class="role-radio" :class="{ on: roleKey === opt.value }"></span>
          <span class="role-card-body">
            <span class="role-card-title">
              {{ opt.text }}
              <span class="text-muted small">· {{ opt.value }}</span>
            </span>
            <span v-if="opt.description" class="role-card-desc">
              {{ opt.description }}
            </span>
          </span>
        </button>
      </div>
    </b-form-group>

    <b-form-group
      label="เหตุผล (อย่างน้อย 10 ตัวอักษร — บันทึกไว้ในประวัติ)"
      label-class="font-weight-bold"
      :invalid-feedback="reasonError"
      :state="reasonState"
    >
      <b-form-textarea
        v-model="reason"
        rows="3"
        :state="reasonState"
        placeholder="ระบุเหตุผลในการมอบบทบาท..."
      />
      <div class="reason-templates mt-2">
        <b-badge
          v-for="tpl in reasonTemplates"
          :key="tpl"
          variant="light"
          class="reason-template-chip"
          @click="applyTemplate(tpl)"
        >
          + {{ tpl }}
        </b-badge>
      </div>
    </b-form-group>

    <b-alert v-if="errorMessage" show variant="danger" class="mt-2 mb-0">
      {{ errorMessage }}
    </b-alert>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" :disabled="isSubmitting" @click="cancel">
        ยกเลิก
      </b-button>
      <b-button
        variant="primary"
        :disabled="!canSubmit || isSubmitting"
        @click="ok"
      >
        <b-spinner v-if="isSubmitting" small class="mr-1" />
        ยืนยัน
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import { ROLE_LABELS_TH, ROLE_DESCRIPTIONS_TH } from "@/constants/roles";

export default {
  name: "AssignRoleDialog",
  data() {
    return {
      // 'branch' or 'clinic'
      scope: "branch",
      targetUser: null,
      branchUrl: null,
      branchId: null,
      clinicUrl: null,
      roleKey: null,
      reason: "",
      isSubmitting: false,
      errorMessage: "",
      reasonTemplates: ["เลื่อนตำแหน่ง", "ทดแทนชั่วคราว", "เพิ่มสาขาใหม่", "ปรับโครงสร้างทีม"],
    };
  },
  computed: {
    ...mapGetters({
      assignableRolesGetter: "moduleUser/assignableRoles",
    }),
    title() {
      return this.scope === "clinic"
        ? "มอบบทบาทระดับคลินิก"
        : "มอบบทบาทในสาขา";
    },
    // Selectable role cards (replaces the old <b-form-select> options).
    roleChoices() {
      const keys = this.assignableRolesGetter(this.scope);
      return keys.map(k => ({
        value: k,
        text: ROLE_LABELS_TH[k] || k,
        description: ROLE_DESCRIPTIONS_TH[k] || "",
      }));
    },
    reasonState() {
      if (this.reason.length === 0) return null;
      return this.reason.trim().length >= 10;
    },
    reasonError() {
      if (this.reason.length > 0 && this.reason.trim().length < 10) {
        return "กรุณาระบุเหตุผลอย่างน้อย 10 ตัวอักษร";
      }
      return "";
    },
    canSubmit() {
      return this.roleKey && this.reason.trim().length >= 10;
    },
  },
  methods: {
    show(payload) {
      // payload: { scope, targetUser, clinicUrl, branchUrl?, branchId? }
      this.scope = payload.scope || "branch";
      this.targetUser = payload.targetUser || null;
      this.clinicUrl = payload.clinicUrl;
      this.branchUrl = payload.branchUrl || null;
      this.branchId = payload.branchId || null;
      this.roleKey = null;
      this.reason = "";
      this.errorMessage = "";
      this.isSubmitting = false;
      this.$bvModal.show("AssignRoleDialog");
    },
    close() {
      this.$bvModal.hide("AssignRoleDialog");
    },
    onHide() {
      // Reset state when dialog closes for any reason.
      this.errorMessage = "";
      this.isSubmitting = false;
    },
    // Prefill the reason box from a template chip; keep anything already typed.
    applyTemplate(tpl) {
      const base = this.reason.trim();
      this.reason = base ? `${base} ${tpl}` : tpl;
    },
    async submit() {
      if (!this.canSubmit || this.isSubmitting) return;
      this.errorMessage = "";
      this.isSubmitting = true;
      try {
        const url =
          this.scope === "clinic"
            ? `/api/userMgmt/${this.clinicUrl}/central-members/${this.targetUser.uid}/roles`
            : `/api/userMgmt/${this.clinicUrl}/branch/${this.branchUrl}/members/${this.targetUser.uid}/roles`;
        await axios.post(url, {
          roleKey: this.roleKey,
          reason: this.reason.trim(),
        }, { skipGlobalErrorHandler: true });
        this.$emit("assigned", {
          scope: this.scope,
          uid: this.targetUser.uid,
          roleKey: this.roleKey,
        });
        this.close();
      } catch (err) {
        this.errorMessage =
          err.response?.data?.errorMessage?.message ||
          err.response?.data?.message ||
          err.message ||
          "ไม่สามารถมอบบทบาทได้";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.target-strip {
  background: #f6f8fb;
  border: 1px solid #e3e8ef;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
}

.role-card-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.role-card {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  width: 100%;
  text-align: left;
  padding: 0.625rem 0.75rem;
  border: 1px solid #dce1e8;
  border-radius: 0.5rem;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease, box-shadow 0.12s ease;
}

.role-card:hover {
  border-color: #b9c4d4;
}

.role-card.selected {
  border-color: #2f5b8c;
  background: #f0f5fb;
  box-shadow: 0 0 0 1px #2f5b8c inset;
}

.role-radio {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border: 2px solid #b9c4d4;
  border-radius: 50%;
  display: grid;
  place-items: center;
}

.role-radio.on {
  border-color: #2f5b8c;
}

.role-radio.on::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2f5b8c;
}

.role-card-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.role-card-title {
  font-weight: 600;
}

.role-card-desc {
  font-size: 0.8125rem;
  color: #6b7280;
  margin-top: 0.125rem;
}

.reason-templates {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.reason-template-chip {
  cursor: pointer;
  border: 1px solid #dce1e8;
  font-weight: 500;
}

.reason-template-chip:hover {
  background: #eef2f7;
}
</style>
